import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useBrand } from '../contexts/BrandContext.jsx';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import { api } from '../services/api/client.js';

// The normal dashboard needs a workspace for New post / Connect, but a person
// publishing their own content does not need the business interview. Creation
// stays an explicit action, never a dashboard mount effect.
export function usePersonalPublishingStart(enabled) {
  const { user } = useAuth();
  const brand = useBrand();
  const dash = useDashboardData();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const current = useRef(null);
  current.current = { userId: user?.id, enabled };
  const mounted = useRef(false);
  const pending = useRef(null);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { pending.current = null; setBusy(false); setError(false); }, [user?.id]);
  useEffect(() => {
    if (!enabled) return;
    // Refresh on entry, including before the two-minute cache expires. The
    // provider discards older reads and clears an allowance on fresh failure.
    Promise.resolve(dash.invalidateResource?.('entitlements', { fresh: true })).catch(() => {});
  }, [enabled, dash.invalidateResource, user?.id]);

  const start = async destination => {
    if (!enabled || pending.current || user?.id == null) return;
    const owner = user.id;
    const request = { owner };
    const isCurrent = () => mounted.current && current.current.userId === owner && current.current.enabled;
    pending.current = request; setBusy(true); setError(false);
    try {
      let brandId = dash.setupStatus?.brand === 'present' ? brand.activeBrandId : null;
      if (!brandId) {
        const response = await api.onboarding.createPersonalWorkspace();
        if (!isCurrent()) return;
        const saved = response?.data || response;
        if (!saved?.brandProfileId) throw new Error('Missing saved workspace');
        brandId = saved.brandProfileId;
        await brand.refresh();
        if (!isCurrent()) return;
        await dash.ensureDashboardData?.({ force: true, keys: ['entitlements', 'setupStatus'] });
      }
      if (!isCurrent()) return;
      navigate(destination === 'create' ? `/app/create?brand=${encodeURIComponent(brandId)}` : '/app/settings?tab=social');
    } catch {
      if (isCurrent()) setError(true);
    } finally {
      if (pending.current === request) pending.current = null;
      if (mounted.current && current.current.userId === owner) setBusy(false);
    }
  };
  return { start, busy, error, disabled: busy || !enabled || user?.id == null };
}
